import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Property } from '../domain/property';
import { withCache, HttpCacheManager } from '@ngneat/cashew';
import { Mention } from '../domain/mention';
import { FollowUp } from '../domain/follow-up';
import { Observable } from 'rxjs';
import { User } from '../domain/user';
import { Role } from '../domain/role';
import { AiSummary } from '../domain/Ai/ai-summary';
import { NewSignup } from '../domain/new-signup';
import { PhobosAiRunLog } from '../domain/phobos-ai-run-log';
import { UpdateBillingDetails } from '../domain/update-billing-details';
import { HttpApiServiceBase } from './http-api-service-base';
import { Organisation } from '../domain/corporate/organisation';

@Injectable({
   providedIn: 'root'
})
/**
 * Note PropertyService can't extend HttpServiceParent because it will cause a circular dependency
 */
export class PropertyService extends HttpApiServiceBase {
   private static readonly AI_SUMMARY_TTL_MS = 5 * 60 * 1000;
   constructor(
      private http: HttpClient,
      private httpCacheManager: HttpCacheManager
   ) {
      super();
   }

   getAiRunLog(propId: number): Observable<PhobosAiRunLog[]> {
      return this.http.get<PhobosAiRunLog[]>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/ai/runlog`
      );
   }

   getAiSummary(propId: number): Observable<AiSummary> {
      return this.http.get<AiSummary>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/ai/summary`,
         {
            context: withCache({ ttl: PropertyService.AI_SUMMARY_TTL_MS })
         }
      );
   }

   get(
      GetAll: boolean = false,
      GetAllForUser: boolean = true,
      IncludeOrganization: boolean = true,
      IncludeOrganizationTasks: boolean = true,
      IncludePms: boolean = true
   ): Observable<Property[]> {
      return this.http.get<Property[]>(`${environment.apiEndpoint}/property`, {
         params: {
            GetAll: GetAll,
            GetAllForUser: GetAllForUser,
            IncludeOrganization: IncludeOrganization,
            IncludeOrganizationTasks: IncludeOrganizationTasks,
            IncludePms: IncludePms
         }
      });
   }

   getMentions(propId: number): Observable<Mention[]> {
      return this.http.get<Mention[]>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/mention`
      );
   }

   getFollowUp(propId: number): Observable<FollowUp[]> {
      return this.http.get<FollowUp[]>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/followup`
      );
   }

   addFollowUp(propId: number, passedFollowup: FollowUp): Observable<FollowUp> {
      return this.http.post<FollowUp>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/followup`,
         passedFollowup,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   completeFollowUps(propId: number, followUpIds: number[]): Observable<number> {
      return this.http.post<number>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(
            propId
         )}/followup/complete`,
         followUpIds,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   readMentions(propId: number, mentionIds: number[]): Observable<number> {
      return this.http.post<number>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(
            propId
         )}/mention/read`,
         mentionIds,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   deleteMentions(propId: number, mentionIds: number[]): Observable<number> {
      return this.http.post<number>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(
            propId
         )}/mention/delete`,
         mentionIds,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getUsers(propId: number): Observable<User[]> {
      return this.http.get<User[]>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/user`
      );
   }

   updateProperty(propId: number, value: boolean): Observable<Property> {
      return this.http.put<Property>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}`,
         {
            hasCompletedOnBoarding: value
         },
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getRoles(propId: number): Observable<Role[]> {
      return this.http.get<Role[]>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/role`,
         {
            context: withCache()
         }
      );
   }

   getNewSignups(): Observable<NewSignup[]> {
      return this.http.get<NewSignup[]>(
         `${environment.apiEndpoint}/property/new-signups`
      );
   }

   updateBillingDetails(
      propId: number,
      details: UpdateBillingDetails
   ): Observable<any> {
      return this.http.patch<any>(
         `${environment.apiEndpoint}/property/update-billing-details/${encodeURIComponent(propId)}`,
         details,
         {
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   setActiveStatus(propId: number, isActive: boolean): Observable<any> {
      return this.http.post<any>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/set-active-status`,
         null,
         {
            params: {
               isActive: isActive
            },
            headers: this.headers,
            responseType: 'json'
         }
      );
   }

   getOrganisations(propId: number): Observable<Organisation[]> {
      return this.http.get<Organisation[]>(
         `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/organisation`
      );
   }

   invalidateAiSummaryCache(propId: number): void {
      const url = `${environment.apiEndpoint}/property/${encodeURIComponent(propId)}/ai/summary`;
      this.httpCacheManager.delete(url);
   }
}
